import { useRoute } from '#imports'
import { ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import {
  type CollectEmailBody,
  postCRMLead,
} from '@backmarket/http-api/src/api-specs-crm-service/crm-service/leads'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import translations from './EmailCollector.translations'
import type { EmailSource } from './EmailCollector.types'

function buildBody({
  email,
  source,
}: {
  email: string
  source: EmailSource
}): CollectEmailBody {
  const { market } = useMarketplace()
  const route = useRoute()
  const routeName = (route.params?.pageName || route.name) as string

  return {
    email,
    subscriptionSource: `${source}_${routeName}`,
    optin: true,
    languageCode: market.defaultLocale.split('_')[0],
    countryCode: market.countryCode,
  }
}

function useEmailCollectorToast({
  toastTitle,
  toastMessage,
}: {
  toastTitle: string
  toastMessage: string
}) {
  const { openSuccessToast, openErrorToast } = useTheToast()
  const openEmailCollectorSuccessToast = () =>
    openSuccessToast({
      title: toastTitle,
      content: toastMessage,
    })

  return { openSuccessToast: openEmailCollectorSuccessToast, openErrorToast }
}

export function useCollectEmail() {
  const isLoading = ref(false)

  const emailCollected = ref(false)

  function track(trackingData: Tracking) {
    const { trackClick } = useTracking()
    trackClick(trackingData || {})
  }
  async function collectEmail({
    email,
    trackingData,
    toastText,
    source,
  }: {
    email: string
    trackingData: Tracking
    toastText?: {
      successTitle: string
      successMessage: string
    }
    source: EmailSource
  }) {
    const i18n = useI18n()

    isLoading.value = true

    track(trackingData)

    const toastTitle =
      toastText?.successTitle || i18n(translations.toastSuccessTitle)
    const toastMessage =
      toastText?.successMessage || i18n(translations.toastSuccessMessage)

    const { openErrorToast, openSuccessToast } = useEmailCollectorToast({
      toastTitle,
      toastMessage,
    })

    try {
      const body = buildBody({ email, source })
      await $httpFetch(postCRMLead, { body })
      openSuccessToast()
      emailCollected.value = true
    } catch (err) {
      const logger = useLogger()
      const error = HttpApiError.fromAnyError(err as Error)

      let errorType = ''
      if (error.status === 0) {
        errorType = 'network'
      } else if (error.status >= 500) {
        errorType = 'api_5xx'
      } else {
        errorType = 'api_4xx'
      }

      logger.error('[LIF][CRM][EmailCollector] Failed to collect email', {
        error,
        feature: 'braze-crm-integration',
        crm_action: 'collect_email_lead',
        api_endpoint: '/crm/leads',
        error_type: errorType,
        error_status: error.status,
        owners: ['bot-squad-lifecycle-front'],
      })

      openErrorToast()
    } finally {
      isLoading.value = false
    }
  }

  return {
    collectEmail,
    isLoading,
    submitButtonIsDisabled: emailCollected,
    emailCollected,
  }
}
